import React, { useState } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Message,
  Select,
  Text,
  Textarea,
} from '@workwhile/ui'
import { Header } from 'components/Header'
import { useHttpServicesContextValue } from '../context/HttpServicesProvider'
import { ResponsePanel } from './ResponsePanel'

const PATH_PARAM_REGEX = /\{(\w+)\}/g

const toOption = (value: string) => ({ label: value, value })

const parseJson = (text: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const pathParamsOf = (urlPattern: string) =>
  Array.from(
    new Set(Array.from(urlPattern.matchAll(PATH_PARAM_REGEX), (m) => m[1]))
  )

type RequestForm = {
  serviceName: string | null
  factoryArgs: Record<string, string>
  urlPattern: string
  urlPatternParams: Record<string, string>
  queryString: string
  graphqlQuery: string | null
  graphqlVariables: string
}

const EMPTY_FORM: RequestForm = {
  serviceName: null,
  factoryArgs: {},
  urlPattern: '/',
  urlPatternParams: {},
  queryString: '',
  graphqlQuery: null,
  graphqlVariables: '{}',
}

export const HttpServicesMain = () => {
  const {
    state: { services, response, isExecuting, executeError },
    actions: { execute },
  } = useHttpServicesContextValue()

  const [form, setForm] = useState<RequestForm>(EMPTY_FORM)
  const update = (patch: Partial<RequestForm>) =>
    setForm((prev) => ({ ...prev, ...patch }))
  const {
    serviceName,
    factoryArgs,
    urlPattern,
    urlPatternParams,
    queryString,
    graphqlQuery,
    graphqlVariables,
  } = form

  const service = services.find((s) => s.name === serviceName)
  const pathParams = pathParamsOf(urlPattern)
  const endpoints = service?.getEndpoints ?? []
  const endpointOptions = endpoints.map((endpoint) => ({
    label: `${endpoint.methodName}  ${endpoint.urlPattern}`,
    value: endpoint.graphqlQuery ?? endpoint.urlPattern,
  }))
  const parsedGraphqlVariables = parseJson(graphqlVariables)

  const handleEndpointChange = (value: string | null) => {
    const endpoint = endpoints.find(
      (e) => (e.graphqlQuery ?? e.urlPattern) === value
    )
    update({
      urlPattern: endpoint?.urlPattern ?? '/',
      graphqlQuery: endpoint?.graphqlQuery ?? null,
      graphqlVariables: JSON.stringify(
        Object.fromEntries(
          (endpoint?.graphqlVariables ?? []).map((name) => [name, null])
        ),
        null,
        2
      ),
    })
  }

  const handleSend = () => {
    if (!service) return
    execute({
      service: service.name,
      factoryArgs,
      urlPattern,
      urlPatternParams: Object.fromEntries(
        pathParams.map((name) => [name, urlPatternParams[name] ?? ''])
      ),
      queryParams: Object.fromEntries(new URLSearchParams(queryString)),
      ...(graphqlQuery && parsedGraphqlVariables
        ? { graphqlQuery, graphqlVariables: parsedGraphqlVariables }
        : {}),
    })
  }

  return (
    <>
      <Header
        title="Temu Postman"
        subTitle="Issue GET requests through BaseHttpAPI service clients"
      />
      <Box p={4} maxWidth={960}>
        <Field label="Service">
          <Select
            block
            placeholder="Select a service"
            options={services.map((s) => toOption(s.name))}
            value={serviceName ? toOption(serviceName) : null}
            onChange={(option) =>
              setForm({ ...EMPTY_FORM, serviceName: option?.value ?? null })
            }
            aria-label="Service"
          />
        </Field>

        {service ? (
          <>
            <Text fontSize={1} color="lightText" mb={3}>
              {service.module}.{service.clientClass}
            </Text>

            {service.factoryParams.map((param) => (
              <Field
                key={param.name}
                label={`${param.name}: ${param.type}${param.required ? '' : ' (optional)'}`}
              >
                {param.choices ? (
                  <Select
                    block
                    options={param.choices.map(toOption)}
                    value={
                      factoryArgs[param.name]
                        ? toOption(factoryArgs[param.name])
                        : null
                    }
                    onChange={(option) =>
                      update({
                        factoryArgs: {
                          ...factoryArgs,
                          [param.name]: option?.value ?? '',
                        },
                      })
                    }
                    aria-label={param.name}
                  />
                ) : (
                  <Input
                    block
                    value={factoryArgs[param.name] ?? ''}
                    onChange={(e) =>
                      update({
                        factoryArgs: {
                          ...factoryArgs,
                          [param.name]: e.target.value,
                        },
                      })
                    }
                  />
                )}
              </Field>
            ))}

            <Field label="Endpoint">
              <Select
                block
                isClearable
                placeholder="Pick a known endpoint, or type a custom path below"
                options={endpointOptions}
                value={
                  endpointOptions.find(
                    (o) => o.value === (graphqlQuery ?? urlPattern)
                  ) ?? null
                }
                onChange={(option) =>
                  handleEndpointChange(option?.value ?? null)
                }
                aria-label="Endpoint"
              />
            </Field>

            {graphqlQuery ? (
              <Field label="GraphQL variables (JSON)">
                <Textarea
                  value={graphqlVariables}
                  onChange={(e) => update({ graphqlVariables: e.target.value })}
                />
              </Field>
            ) : (
              <Field label="URL pattern">
                <Input
                  block
                  value={urlPattern}
                  onChange={(e) => update({ urlPattern: e.target.value })}
                />
              </Field>
            )}

            {pathParams.map((name) => (
              <Field key={name} label={`{${name}}`}>
                <Input
                  block
                  value={urlPatternParams[name] ?? ''}
                  onChange={(e) =>
                    update({
                      urlPatternParams: {
                        ...urlPatternParams,
                        [name]: e.target.value,
                      },
                    })
                  }
                />
              </Field>
            ))}

            {graphqlQuery ? null : (
              <Field label="Query string">
                <Input
                  block
                  placeholder="limit=10&offset=0"
                  value={queryString}
                  onChange={(e) => update({ queryString: e.target.value })}
                />
              </Field>
            )}

            <Flex justifyContent="flex-end" mt={3}>
              <Button
                onClick={handleSend}
                loading={isExecuting}
                disabled={!parsedGraphqlVariables}
              >
                {'Submit request'}
              </Button>
            </Flex>
          </>
        ) : null}

        {executeError ? (
          <Message variant="error" mt={3} borderRadius="small">
            {executeError}
          </Message>
        ) : null}
        {response && !executeError ? (
          <ResponsePanel response={response} />
        ) : null}
      </Box>
    </>
  )
}
